import { PickSampleExt } from "@novorender/api";
import { vec2, vec3 } from "gl-matrix";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { undoRedoActions } from "features/undoRedo";
import { selectCanvasContextMenuFeatures, selectIsProcoreDesign } from "slices/explorer";
import { isPdfOrDrawingObjectId } from "utils/objectData";

import { getPlaneCreationNormal } from "../planeCreationNormal";
import {
    renderActions,
    selectCameraType,
    selectCanvasSelectionEnabled,
    selectClippingPlanes,
    selectIsXRayActive,
    selectMainObject,
    selectPicker,
    selectSelectMultiple,
} from "../renderSlice";
import { CameraType, Picker, Stamp, StampKind } from "../types";

export function useCanvasContextMenuHandler() {
    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const highlightedObjects = useHighlighted();
    const cameraType = useAppSelector(selectCameraType);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const features = useAppSelector(selectCanvasContextMenuFeatures);
    const picker = useAppSelector(selectPicker);
    const isMultipleSelectionActive = useAppSelector(selectSelectMultiple);
    const {
        state: { view, canvas, db },
    } = useExplorerGlobals();

    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const mainObject = useAppSelector(selectMainObject);
    const canvasSelectionEnabled = useAppSelector(selectCanvasSelectionEnabled);
    const isXRayActive = useAppSelector(selectIsXRayActive);

    const shouldShow = isProcoreDesign ? canvasSelectionEnabled : true;

    const buildStampForResult = (pos: vec2, result: PickSampleExt | undefined): Stamp | null => {
        if (!canvas) {
            return null;
        }
        const canvasRect = canvas.getBoundingClientRect();
        const normal = result ? getPlaneCreationNormal(result.normal) : undefined;

        if (!result || result.objectId === -1) {
            if (cameraType === CameraType.Orthographic && clippingPlanes.planes.length) {
                return {
                    kind: StampKind.CanvasContextMenu,
                    data: {
                        object: result?.objectId ?? undefined,
                        position: result?.position ?? undefined,
                        normal: normal ? [...normal] : undefined,
                    },
                    pinned: true,
                    mouseX: pos[0] + canvasRect.left,
                    mouseY: pos[1] + canvasRect.top,
                };
            }
            return null;
        }

        return {
            kind: StampKind.CanvasContextMenu,
            data: {
                object: result.objectId,
                position: [...result.position] as vec3,
                normal: normal ? [...normal] : undefined,
            },
            pinned: true,
            mouseX: pos[0] + canvasRect.left,
            mouseY: pos[1] + canvasRect.top,
        };
    };

    /**
     * Display the canvas context menu stamp based on an existing pick result,
     * without performing a new pick or modifying the current selection.
     * Used to open the context menu after a pick-selection (e.g. on touch tap).
     */
    const showContextMenuStamp = (pos: vec2, result: PickSampleExt | undefined) => {
        if (!shouldShow || !view || picker !== Picker.Object || !features.length || !canvas || isXRayActive) {
            return;
        }

        const stamp = buildStampForResult(pos, result);
        if (stamp) {
            dispatch(renderActions.setStamp(stamp));
        }
    };

    const handleContextMenu = async (pos: vec2, isTouch: boolean) => {
        if (!shouldShow || !view || picker !== Picker.Object || !features.length || !canvas || isXRayActive) {
            return;
        }

        const result = await view.pick(pos[0], pos[1], {
            sampleDiscRadius: isTouch ? 8 : 4,
        });

        if (!result || result.objectId === -1) {
            const stamp = buildStampForResult(pos, result);
            if (stamp) {
                dispatch(renderActions.setStamp(stamp));
            } else {
                dispatch(renderActions.setStamp(null));
            }
            return;
        }

        // PDFs are excluded from the Procore object tree and are not selectable in 3D
        // there (see useCanvasClickHandler), so they get no object context menu either.
        if (isProcoreDesign && (await isPdfOrDrawingObjectId(view, db, result.objectId))) {
            dispatch(renderActions.setStamp(null));
            return;
        }

        const newStamp = buildStampForResult(pos, result);
        if (!newStamp) {
            return;
        }

        const alreadySelected = highlightedObjects.ids.has(result.objectId);
        if (alreadySelected) {
            // If the object is already selected, just need to update the stamp
            // position. No selection changes.
            dispatch(renderActions.setStamp(newStamp));
            return;
        }

        if (isMultipleSelectionActive) {
            const selectObjectNoStamp = () => {
                dispatch(renderActions.setMainObject(result.objectId));
                dispatchHighlighted(highlightActions.add([result.objectId]));
            };
            const selectObject = () => {
                selectObjectNoStamp();
                dispatch(renderActions.setStamp(newStamp));
            };
            selectObject();

            dispatch(
                undoRedoActions.push({
                    description: "Context Menu Multiselect object",
                    undoCallback: () => {
                        dispatch(renderActions.setMainObject(mainObject));
                        dispatchHighlighted(highlightActions.remove([result.objectId]));
                    },
                    redoCallback: selectObjectNoStamp,
                }),
            );
        } else {
            const selectObjectNoStamp = () => {
                dispatch(renderActions.setMainObject(result.objectId));
                dispatchHighlighted(highlightActions.setIds([result.objectId]));
            };
            const selectObject = () => {
                selectObjectNoStamp();
                dispatch(renderActions.setStamp(newStamp));
            };
            selectObject();

            dispatch(
                undoRedoActions.push({
                    description: "Context Menu Select object",
                    undoCallback: () => {
                        dispatch(renderActions.setMainObject(mainObject));
                        dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
                    },
                    redoCallback: selectObjectNoStamp,
                }),
            );
        }
    };

    return { handleContextMenu, showContextMenuStamp };
}
